import React, { useState, useRef, useEffect } from "react";
import { SITE_URL, getUniqueTabId } from "../settings";
import { apiErrorsHandler, getErrorMessages, api } from "../api/httpclient";
import { useEffectOnce } from "../hooks/useeffectonce";
import { useDispatch, useSelector } from "react-redux";
import { setFaceIO, setLanguage } from "../redux/slices/appslice";
import { login } from "../redux/slices/loginpageslice";
import { setCounter } from "../redux/slices/notificationscounterslice";
import {
  setAppInitCompleted,
  setSignalRLinkedCompleted,
} from "../redux/slices/appinitslice";
import {
  applyMigrationTerminated,
  reapplyMigrationTerminated,
} from "../redux/slices/migrationspageslice";
import { HubConnection, HubConnectionBuilder } from "@microsoft/signalr";
import { setSignalRConnectionId } from "../redux/slices/signalrslice";
import {
  incrementCounter,
  decrementCounter,
} from "../redux/slices/notificationscounterslice";
import {
  notificationAdded,
  markedAsRead,
  markedAsUnread,
  notificationRemoved,
  alertRemoved,
} from "../redux/slices/notificationspageslice";
import {
  reportViewGeneratedSucceeded,
  reportViewGeneratedFailed,
  globalGenerateReport,
  globalCancelReport,
  setProcessing,
} from "../redux/slices/reportingviewpageslice";
import {
  notificationDurationShort,
  notificationDurationMiddle,
} from "../lib/utils";
import { addNotification } from "../redux/slices/notificationsslice";
import { getTilesForGenerateGlobalReport } from "./widgets/utils";
import {
  setWelcomeScreen,
  hideWelcomeScreen,
} from "../redux/slices/welcomescreenslice";
import { setCurrentUser } from "../redux/slices/currentuserslice";
import { setCurrentTheme } from "../redux/slices/currentthemeslice";
import { setAppearance } from "../redux/slices/appearanceslice";
import { useThemeSwitcher } from "react-css-theme-switcher";
import { transformReportsMenu } from "../lib/utils";

import { themes } from "../sass/themes";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const AppInit = (props) => {
  const dispatch = useDispatch();
  const currentTheme = useSelector((state) => state.currentTheme);
  const signalR = useSelector((state) => state.signalR);
  const { switcher, status } = useThemeSwitcher();
  const appInit = useSelector((state) => state.appInit);
  let lang = "en";
  const reportingViewPageState = useSelector(
    (state) => state.reportingViewPage
  );
  const currentUser = useSelector((state) => state.currentUser);
  const _currentUser = useRef(null);
  _currentUser.current = currentUser;
  const _reportingViewPageState = useRef(null);
  _reportingViewPageState.current = reportingViewPageState;

  const [animate, setAnimate] = useState(false);

  const { t } = useTranslation();

  useEffectOnce(() => {
    if (!appInit.isCompleted) {
      //show the loader
      dispatch(
        setWelcomeScreen({
          isVisible: true,
          text: { content: t("appinit.initialize") },
        })
      );

      //initialize SignalR
      const connect = new HubConnectionBuilder()
        .withUrl(
          SITE_URL + "/hubs/connections"
          //  ,{
          // transport: 1,//1 websockets, 4 longpolling, 2 server events and 0 none; user | to combine many values
          // // //     //headers: { "Key": "value" },
          // // //     accessTokenFactory: null,
          // // //     //logMessageContent: true,
          // skipNegotiation: true,//keep it false, otherwise if true, when reconnect, onreconnected' first parameter which is supposed to be the connectionId will be null and we lose the new connectionid
          // // //     withCredentials: true,
          // //      timeout: 100000
          //  }
        )
        //.configureLogging("trace")
        .withAutomaticReconnect()
        .build();

      connect.onreconnecting((error) => {
        //console.log("..1 2 3 4 5 6 7 8 9...ONRECONNECTING", error);
      });

      connect.onreconnected((connectionId) => {
        console.log("onreconnected, connectionId: ", connectionId);

        //let oldConnectionId = signalR.connectionId ?? "";

        dispatch(setSignalRConnectionId(connectionId));

        //reconnect SignalR if the user exists F&F
        if (
          _currentUser &&
          _currentUser.current &&
          _currentUser.current.IsAuthenticated
        ) {
          let oldConnectionId = signalR.connectionId;

          api({
            url: "signalr/reconnectsignalr",
            method: "post",
            data: {
              connectionId: connectionId,
              userId: _currentUser.current.Id,
              tenantId: _currentUser.current.Subscription.Id,
              oldConnectionId: oldConnectionId,
            },
          });
        }
      });

      connect
        .start()
        .then(() => {
          connect.invoke("GetConnectionId").then((connectionId) => {
            dispatch(setSignalRConnectionId(connectionId));

            //connect SignalR if the user exists F&F
            if (
              _currentUser &&
              _currentUser.current &&
              _currentUser.current.IsAuthenticated
            ) {
              api({
                url: "signalr/connectsignalr",
                method: "post",
                data: {
                  userId: _currentUser.current.Id,
                  tenantId: _currentUser.current.Subscription.Id,
                },
              });
            }
          });

          connect.on("CreateNotificationAsync", (message) => {
            //{id, title, type, importantce, status, initiatedOn, initiatedBy(string), alertId, alertSessionDetailId, isInitiator
            // usersIds:[string]}
            let currentUser = _currentUser.current;
            if (!currentUser || !currentUser.IsAuthenticated) return;

            const tenantId = message.tenantId;
            if (!tenantId || currentUser.Subscription.Id != tenantId) return;

            const users = message.usersIds;

            if (users.find((e) => e.toString() == currentUser.Id.toString())) {
              dispatch(
                notificationAdded({
                  Id: message.id,
                  Title: message.title,
                  Type: message.type,
                  Importance: message.importance,
                  Status: message.status,
                  InitiatedOn: message.initiatedOn,
                  InitiatedBy: message.initiatedBy,
                  AlertId: message.alertId,
                  AlertSessionDetailId: message.alertSessionDetailId,
                  IsInitiator: message.isInitiator,
                })
              );

              dispatch(incrementCounter());
            }
          });

          connect.on("SetNotificationStatusAsync", (message) => {
            //{id, status, userId}
            let currentUser = _currentUser.current;
            if (!currentUser || !currentUser.IsAuthenticated) return;

            const tenantId = message.tenantId;
            if (!tenantId || currentUser.Subscription.Id != tenantId) return;

            if (message.userId == currentUser.Id.toString()) {
              if (message.status == 1) {
                //mark as read
                dispatch(markedAsRead(message.id));
                dispatch(decrementCounter());
              } else {
                //mark as unread
                dispatch(markedAsUnread(message.id));
                dispatch(incrementCounter());
              }
            }
          });

          connect.on("RemoveUserNotificationAsync", (message) => {
            //{id, status, userId}
            let currentUser = _currentUser.current;
            if (!currentUser || !currentUser.IsAuthenticated) return;

            const tenantId = message.tenantId;
            if (!tenantId || currentUser.Subscription.Id != tenantId) return;

            if (message.userId == currentUser.Id.toString()) {
              dispatch(notificationRemoved(message.id));

              if (message.status == -1) dispatch(decrementCounter());
            }
          });

          connect.on("RemoveAlertNotificationAsync", (message) => {
            //{alertId, unreadNotifications:[{key: string, value: int}]

            let currentUser = _currentUser.current;
            if (!currentUser || !currentUser.IsAuthenticated) return;

            const tenantId = message.tenantId;
            if (!tenantId || currentUser.Subscription.Id != tenantId) return;

            var entry = message.unreadNotifications.find(
              (e) => e.key == currentUser.Id.toString()
            );
            if (entry) {
              dispatch(alertRemoved(message.alertId));
              dispatch(setCounter(entry.value));
            }
          });

          connect.on(
            "ReportViewGenerationSucceededNotificationAsync",
            (message) => {
              //{userId:, widgetsIds, requestId, data, dataSourceTypeId}]
              console.log("REPORT SUCC PRODUCED", message);

              let currentUser = _currentUser.current;
              if (!currentUser || !currentUser.IsAuthenticated) return;

              const tenantId = message.tenantId;
              if (!tenantId || currentUser.Subscription.Id != tenantId) return;

              //check if message is for current user
              if (currentUser.Id == message.userId)
                dispatch(reportViewGeneratedSucceeded({ ...message }));
            }
          );

          connect.on(
            "ReportViewGenerationFailedNotificationAsync",
            (message) => {
              //{userId:, widgetsIds, requestId, errorText}]
              console.log("REPORT ERROR PRODUCED", message);

              let currentUser = _currentUser.current;
              if (!currentUser || !currentUser.IsAuthenticated) return;

              const tenantId = message.tenantId;
              if (!tenantId || currentUser.Subscription.Id != tenantId) return;

              //check if message is for current user
              if (currentUser.Id == message.userId)
                dispatch(reportViewGeneratedFailed({ ...message }));
            }
          );

          connect.on("DataProcessingSucceededNotificationAsync", (message) => {
            //{userId:, widgetsIds, requestId, data, dataSourceTypeId}]
            console.log("DATA PROCESSING SUCC PRODUCED", message);

            let currentUser = _currentUser.current;
            if (!currentUser || !currentUser.IsAuthenticated) return;

            const tenantId = message.tenantId;
            if (
              !tenantId ||
              currentUser.Subscription.Id?.toLowerCase() !=
                tenantId.toLowerCase()
            )
              return;
            //check if message is for current user
            const users = message.usersIds;

            if (
              users.find(
                (e) =>
                  e.toString()?.toLowerCase() ==
                  currentUser.Id.toString().toLowerCase()
              )
            ) {
              dispatch(
                addNotification({
                  type: "success",
                  text: message.content,
                  duration: notificationDurationShort,
                  closable: true,
                })
              );
              generateLiveReport();
            }
          });

          connect.on("DataProcessingFailedNotificationAsync", (message) => {
            //{userId:, widgetsIds, requestId, errorText}]
            console.log("DATA PROCESSING ERROR PRODUCED", message);

            let currentUser = _currentUser.current;
            if (!currentUser || !currentUser.IsAuthenticated) return;

            const tenantId = message.tenantId;
            if (
              !tenantId ||
              currentUser.Subscription.Id?.toLowerCase() !=
                tenantId.toLowerCase()
            )
              return;

            //check if message is for current user
            const users = message.usersIds;

            if (
              users.find(
                (e) =>
                  e.toString?.toLowerCase() ==
                  currentUser.Id.toString().toLowerCase()
              )
            )
              dispatch(
                addNotification({
                  type: "error",
                  text: message.content,
                  duration: notificationDurationShort,
                  closable: true,
                })
              );
          });

          connect.on("ApplyMigrationNotificationAsync", (message) => {
            //{userId:, widgetsIds, requestId, data, dataSourceTypeId}]
            console.log("APPLY MIGRATION PRODUCED", message);

            let currentUser = _currentUser.current;
            if (!currentUser || !currentUser.IsAuthenticated) return;

            const tenantId = message.tenantId;
            if (!tenantId || currentUser.Subscription.Id != tenantId) return;

            //check if message is for current user
            if (currentUser.Id == message.userId)
              dispatch(applyMigrationTerminated({ ...message }, t));
          });

          connect.on("ReapplyMigrationNotificationAsync", (message) => {
            //{userId:, widgetsIds, requestId, data, dataSourceTypeId}]
            console.log("REAPPLY MIGRATION PRODUCED", message);

            let currentUser = _currentUser.current;
            if (!currentUser || !currentUser.IsAuthenticated) return;

            const tenantId = message.tenantId;
            if (!tenantId || currentUser.Subscription.Id != tenantId) return;

            //check if message is for current user
            if (currentUser.Id == message.userId)
              dispatch(reapplyMigrationTerminated({ ...message }, t));
          });
        })
        .catch((error) => console.log(error));
      //end of SignslR Init

      api({
        url: "auth/appinit",
        method: "get",
      })
        .then((response) => {
          //returns the user object
          let data = response.data.Data;

          //set the current user
          dispatch(
            setCurrentUser({
              ...data,
              ReportsMenu: transformReportsMenu(data.ReportsMenu),
            })
          );

          //set the current theme
          dispatch(setCurrentTheme(themes[data.ThemeId.toString()]));
          switcher({ theme: themes[data.ThemeId.toString()] });

          //set appearance 
          // if (appearance.isRightToLeft != data.IsRightToLeft) {
          //   dispatch(
          //     setAppearance({ isRightToLeft: appearance.isRightToLeft })
          //   );
          // } else {
          //   dispatch(setAppearance({ isRightToLeft: data.IsRightToLeft }));
          // }
          dispatch(setAppearance({ isRightToLeft: data.IsRightToLeft }));
          switch (data.LanguageId) {
            case 1:
              lang = "en";
              break;
            case 2:
              lang = "ar";
              break;
            case 3:
              lang = "fr";
              break;
            default:
              lang = "en";
          } 
          dispatch(setLanguage(lang));

          if (data.IsAuthenticated) {
            dispatch(setCounter(data.UnreadNotificationsCount));

            //connect SignalR F&F
            api({
              url: "signalr/connectsignalr",
              method: "post",
              data: {
                userId: data.Id,
                tenantId: data.Subscription.Id,
              },
            })
              .then((e) => {
                console.log("CONNECTING SIGNALR THEN");
                dispatch(setSignalRLinkedCompleted(true));
              })
              .catch((e) => {
                console.log("CONNECTING SIGNALR CATCH");
                dispatch(setSignalRLinkedCompleted(true));
              });
          }

          //hide the loader
          dispatch(hideWelcomeScreen());

          //re-render
          dispatch(setAppInitCompleted(true));
          //setAppInitCompleted(true);
        })
        .catch((err) => {
          //hide the loader
          dispatch(hideWelcomeScreen());

          //re-render anyway
          if (!appInit.isCompleted) dispatch(setAppInitCompleted(true));
          //setAppInitCompleted(true);
        });
    }

    return () => {
      //dispatch(setFaceIO(null))
    };
  }, []);

  const generateLiveReport = () => {
    let reportingViewPageState = _reportingViewPageState.current;

    if (
      !reportingViewPageState.pageLoaded ||
      reportingViewPageState.pageDisposed ||
      !reportingViewPageState.isLiveReport
    )
      return;

    if (
      !reportingViewPageState.globalFilter ||
      !reportingViewPageState.globalFilter.isVisible ||
      reportingViewPageState.globalGenerate
    )
      // reportingViewPageState.globalRequestId)
      return;

    if (reportingViewPageState.tiles.find((e) => e.requestId != null)) return;

    const newRequestId =
      Math.ceil(Math.random() * 100000000 + 1).toString() +
      Date.now().toString(); // (new Date()).toString();

    let tiles = getTilesForGenerateGlobalReport(
      reportingViewPageState,
      newRequestId
    );

    dispatch(globalGenerateReport(tiles)).then(
      (response) => {
        //dispatch(setPageCommands(commands));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));
        dispatch(globalCancelReport());

        //do not show notification if failed because timeout and global request id is not newRequestId
        if (
          err &&
          err.succeeded == false &&
          err.timedOut &&
          reportingViewPageState.globalRequestId != newRequestId
        )
          return;

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("appinit.loading_errormessage"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              })
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              })
            );
          }
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("appinit.unknown_error"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
        };

        apiErrorsHandler({}, dispatch, null, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  };

  return (
    <React.Fragment>
      {
        //render the children if no connection (in this case user will be anonymous or when appInit Api returns)
        (appInit.isCompleted || (navigator && navigator.onLine == false)) &&
          props.children
      }
      <style>
        {`
                ::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                    
                /* Track */
                ::-webkit-scrollbar-track {
                    background-color: ${currentTheme.scrollBarTrackColor};
                    height:8px !important;
                    /*border-radius: 4px;*/
                    /*border-left: 1px solid ${currentTheme.componentBorderColor};*/
                }
                
                /* Handle */
                ::-webkit-scrollbar-thumb {
                    background-color: ${currentTheme.scrollBarThumbColor};/*scrollBarThumbColor*/
                    /*background-color: transparent;*/
                    /*border-top: 12px solid ${currentTheme.scrollBarThumbHoverColor}*/ /*or any height and color you want*/;
                    /*border-radius: 4px;*/
                }
                
                /* Handle on hover */
                ::-webkit-scrollbar-thumb:hover {
                    background-color: ${currentTheme.scrollBarThumbHoverColor};
                }


                /*style the <body>*/
                body
                {
                    background-color: ${currentTheme.bodyBg};
                    background-image: ${currentTheme.bodyBgImage};
                }
                /*style the <body>*/


                /*a useful class that can be used in any page*/
                .menu-separator
                {
                    width: 100%;
                    padding: 0 !important;
                    margin: 0 !important;
                    border-top: 1px solid ${currentTheme.componentBorderColor};
                }


                /*input*/
                .k-input, .k-input:focus, .k-dropdownlist, .k-dropdownlist:focus
                .k-dialog .k-dropdownlist, .k-dialog .k-dropdownlist:focus
                {
                    background-color: ${currentTheme.componentBg};
                    border-color: ${currentTheme.componentBorderColor};        
                    box-shadow: none !important;               
                } 

                .k-checkbox, .k-checkbox:focus, .k-radio, .k-radio:focus,
                {
                    border-color: ${currentTheme.componentBorderColor};        
                    box-shadow: none !important;               
                } 

                .k-button.k-icon-button
                {
                    /*the arraw of the dropdown or the icon that replaces the arrow of the dropdown*/
                    color: ${currentTheme.touchColor};
                }
                /*input*/


                /*dialog form toolbar*/
                .toolbar > .k-toolbar
                {
                    background-color: ${currentTheme.toolbarBg};
                }

                 .toolbar > .k-toolbar .k-button-text
                {
                    color: ${currentTheme.toolbarText} !important;
                    font-weight: 500 !important;
                    font-size: 13px;
                }

                .toolbar > .k-toolbar .k-button:hover .k-button-text
                {
                    color: ${currentTheme.toolbarHoverText} !important;
                }                                 
                
                .toolbar > .k-toolbar .k-button:hover .k-icon
                {
                    color: ${currentTheme.toolbarHoverText} !important;
                }

                .toolbar > .k-toolbar .k-button:hover svg
                {
                     fill: ${currentTheme.toolbarHoverText} !important;
                }
                /*dialog form toolbar*/


                /*dialog*/
                .k-dialog-titlebar.k-header
                {
                    background-color: ${currentTheme.dialogToolbarBg} !important;
                    color: ${currentTheme.dialogTextColor};
                }
                /*dialog*/


                .external-gauge-icon > path
                {
                    fill: ${currentTheme.primary};
                }


                /*toolbar in dialog*/
                .k-dialog
                {
                    border: ${currentTheme.dialogBorder};
                    border-radius: 6px;
                }

                .k-dialog .toolbar, .k-dialog .toolbar .k-toolbar
                {
                    width: 100%;
                }

                .k-dialog .toolbar .k-toolbar
                {
                    background-color: ${currentTheme.dialogToolbarBg} !important;
                    padding: 5px 4px 5px 4px!important;
                }

                .k-dialog .toolbar .k-toolbar .k-button-icon
                {
                    color: ${currentTheme.dialogToolbarText} !important;
                    padding: 5px 4px 5px 4px!important;
                }

                .k-dialog .k-toolbar .k-button-text
                {
                    color: ${currentTheme.dialogToolbarText} !important;
                    font-weight: 500 !important;
                    font-size: 13px!important;
                }

                .k-dialog .toolbar > .k-toolbar .k-button:hover .k-button-text
               
                {
                    color: ${currentTheme.dialogToolbarHoverText} !important;
                    font-size: 13px!important;
                    font-weight: 500 !important;
                }

                .k-dialog .toolbar > .k-toolbar .k-button:hover .k-icon
                {
                    color: ${currentTheme.dialogToolbarHoverText} !important;
                }

                .k-dialog .k-dialog-buttongroup
                {
                    border-color: ${currentTheme.separatorColor};
                }

                .k-popup .k-menu-item
                {
                    padding:4px 18px 4px 12px;
                }

                .k-popup .k-menu-item:hover,
                .k-popup .k-menu-item:hover .k-menu-link
                {
                    background-color: ${currentTheme.hoveredBg};
                }
              
                /*grid*/
                .k-grid 
                {
                    border: ${currentTheme.gridBorder};
                }

                .k-grid .k-grid-content
                {
                    /*overflow-y: auto;*/
                }

                .k-grid table thead tr
                {
                    background-color: ${currentTheme.gridHeaderBg};
                    color: ${currentTheme.gridHeaderColor};
                }

                .k-grid .k-master-row > td
                {
                    border-bottom: 1px solid ${currentTheme.componentBorderColor};
                }

                .k-grid .k-master-row:last-child > td
                {
                    border-bottom: none;
                }

                .k-grid .k-master-row.k-alt,
                .k-grid .k-master-row.k-alt:hover,
                .k-grid .k-master-row:hover
                {
                    background-color: ${currentTheme.componentBg};
                }   
                     
                .custom-donut-chart .k-chart-surface .k-chart-legend-item text {
                  fill: ${currentTheme.primary} ;
                }
          `}
      </style>
    </React.Fragment>
  );
};
